import { useQuery } from '@tanstack/react-query';
import { repositorioCctv } from '../../../data/repositories/RepositorioCctv';
import { useReproductorHls } from '../../hooks/useReproductorHls';
import estilos from './Panel.module.css';

interface FondoCamaraProps {
  camaraId: number;
}

/**
 * Una cámara en vivo como fondo del panel.
 *
 * Es la alternativa al plano fijo: en una instalación pequeña, ver la nave de
 * verdad con los sensores encima dice más que un dibujo. Los marcadores se
 * colocan igual, en porcentaje, así que siguen cuadrando aunque el vídeo tenga
 * otras proporciones que la imagen que hubiera antes.
 *
 * Si la cámara ya no existe —la borraron, o quien mira el panel dejó de tener
 * acceso a ella— se dice, en vez de dejar un rectángulo negro sin explicación.
 */
export function FondoCamara({ camaraId }: FondoCamaraProps) {
  const camaras = useQuery({
    queryKey: ['cctv', 'camaras', null],
    queryFn: () => repositorioCctv.listarCamaras(null),
    staleTime: 60_000,
  });

  const camara = camaras.data?.find((c) => c.id === camaraId) ?? null;
  const { video, estado } = useReproductorHls(camara?.enlaceHls ?? null);

  if (camaras.isLoading) return null;

  if (!camara) {
    return (
      <div className={estilos.fondoVacio}>
        <div className={estilos.fondoVacioIcono}>📷</div>
        <div className={estilos.fondoVacioTitulo}>Esa cámara ya no está disponible</div>
        <div className={estilos.fondoVacioTexto}>
          La borraron o dejaste de tener acceso a ella. Entra en <b>Editar</b> para elegir otra.
        </div>
      </div>
    );
  }

  return (
    <>
      <video ref={video} autoPlay muted playsInline className={estilos.videoFondo} />

      {/* Sólo se avisa cuando algo va mal; en marcha normal el vídeo habla solo. */}
      {estado !== 'enVivo' && (
        <span className={estilos.estadoFondo}>
          {estado === 'sinSenal' ? 'Sin señal' : 'Conectando…'}
        </span>
      )}
    </>
  );
}
